//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import {
  linkProductToBoundingBoxV1,
  linkProductToBoundingBoxV2,
} from '@apis/intelligent-product-tagging/intelligentProductTaggingApi';
import { saveProductTags } from '@apis/universal-image-actions/universalImageActionsApi';
import Cuid from '@models/Cuid';
import IItemMetadata, {
  Product,
  UiaProductTagRequest,
} from '@models/IItemMetadata';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import {
  convertProducts,
  formatProducts,
  sortProducts,
  validateProducts,
} from '@uia/details/utils/productTagsUtils';
import { TagLocation } from '@uia/products/constants/UiaProductConstants';
import {
  getIsIntelligentProductTaggingEnabled,
  getIsIntelligentProductTaggingV2Enabled,
} from '@uia/products/selectors/getUIAReactPortItemProducts';
import { isDefined } from '@utils/filterPredicates';

interface BaseMutationParams {
  cuid: Cuid;
  broadcast: (name: string, item: object) => void;
  onSuccess?: (data: Product[], variables: MutationVariables) => void;
  onError?: (error: unknown) => void;
  trackProductTaggingEvent?: (product: UiaProductTagRequest) => void;
}

interface TagVariables {
  boxId?: string;
  isCatalogSuggestions?: boolean;
  mutationType: 'add';
  newProducts: UiaProductTagRequest[];
  productList: Product[];
  searchResultsProductPosition?: number;
  tagLocation: TagLocation;
}

interface EditVariables {
  mutationType: 'edit';
  updatedProduct: UiaProductTagRequest;
  productList: Product[];
}

interface RemoveVariables {
  mutationType: 'remove';
  productStyleId: string;
  productList: Product[];
}

interface RemoveAllVariables {
  mutationType: 'removeAll';
  productList: Product[];
}

interface ReorderVariables {
  mutationType: 'reorder';
  productList: Product[];
}

export type MutationVariables =
  | TagVariables
  | EditVariables
  | RemoveVariables
  | ReorderVariables
  | RemoveAllVariables;

export interface MutationContext {
  previousMetadata: IItemMetadata | undefined;
}

export const useProductTagsMutation = ({
  broadcast,
  cuid,
  onError,
  onSuccess,
  trackProductTaggingEvent,
}: BaseMutationParams) => {
  const queryClient = useQueryClient();
  // TODO: PD-301920 - Cleanup feature flag once IPTv2 GAed and IPTv1 is decommissioned
  const isIntelligentProductTaggingEnabled = useSelector(
    getIsIntelligentProductTaggingEnabled,
  );
  const isIntelligentProductTaggingV2Enabled = useSelector(
    getIsIntelligentProductTaggingV2Enabled,
  );
  const queryKey = [
    QueryKeyConstants.UniversalImageActions,
    QueryKeyConstants.UniversalImageActionsRights,
    QueryKeyConstants.UniversalImageActionsMetadata,
    cuid,
  ];
  const partialCuid = Cuid.toPartial(cuid);

  return useMutation<
    Product[],
    unknown,
    MutationVariables,
    MutationContext | undefined
  >({
    mutationFn: async (variables: MutationVariables) => {
      const productList = convertProducts(variables.productList);
      let updatedProductTags: UiaProductTagRequest[] = [];

      switch (variables.mutationType) {
        case 'add': {
          const { newProducts } = variables;
          if (trackProductTaggingEvent) {
            newProducts.forEach(trackProductTaggingEvent);
          }
          updatedProductTags = [...productList, ...newProducts];
          break;
        }
        case 'edit': {
          const { updatedProduct } = variables;
          updatedProductTags = productList.map((existingProduct) =>
            existingProduct.productStyleId === updatedProduct.productStyleId
              ? { ...updatedProduct, isUpdated: true } // to edits we need to mark the updated product as `isUpdated` or it will not save the updates
              : existingProduct,
          );
          break;
        }
        case 'remove': {
          const { productStyleId } = variables;
          updatedProductTags = productList.filter(
            (existingProduct) =>
              existingProduct.productStyleId !== productStyleId,
          );
          break;
        }
        case 'removeAll': {
          updatedProductTags = [];
          break;
        }
        case 'reorder': {
          updatedProductTags = productList;
          break;
        }
      }

      const formattedProductTags = formatProducts(updatedProductTags);
      const error = validateProducts(formattedProductTags);

      if (isDefined(error)) {
        broadcast('uia:invalid-products', {
          errorMessage: error,
          products: formattedProductTags,
          validationError: true,
        });

        throw new Error(error);
      }

      const res = await saveProductTags({
        cuid: partialCuid,
        products: formattedProductTags,
      });

      const savedAndSortedProducts = sortProducts(res.products, 'position');

      /**
       * If the product was added via clicking IPT bounding box, record that the
       * client linked a product to the bounding box.
       * During the v1 to v2 migration, both flags may be enabled simultaneously.
       * So the check below determines which API version to call.
       * TODO: PD-301920 - Cleanup feature checks code once IPTv2 GAed and IPTv1 is decommissioned
       */
      if (
        variables.mutationType === 'add' &&
        (isIntelligentProductTaggingEnabled ||
          isIntelligentProductTaggingV2Enabled)
      ) {
        const { boxId, newProducts } = variables;
        if (
          boxId &&
          newProducts.length === 1 &&
          newProducts[0]?.productStyleId
        ) {
          const firstNewProductStyleId = newProducts[0].productStyleId;
          const networkItemId = partialCuid;

          // TODO: PD-301920 - Cleanup the linkProductToBoundingBoxV1 method and the feature check once IPTv2 GAed
          if (isIntelligentProductTaggingV2Enabled) {
            // Set position to -1 if results are not from IPT suggestions
            const position = variables.isCatalogSuggestions
              ? variables.searchResultsProductPosition ?? -1
              : -1;

            void linkProductToBoundingBoxV2({
              boxId,
              networkItemId,
              position,
              styleId: firstNewProductStyleId,
            });
          } else {
            void linkProductToBoundingBoxV1({
              boxId,
              networkItemId,
              productId: firstNewProductStyleId,
            });
          }
        }
      }

      // Broadcast the change so the spatial tag positions can be updated
      broadcast('uia:products-changed', savedAndSortedProducts);

      return savedAndSortedProducts;
    },
    onError: (error, variables, context) => {
      // Rollback to the previous value
      if (context?.previousMetadata) {
        queryClient.setQueryData(queryKey, context.previousMetadata);
      }
      if (onError) {
        onError(error);
      }
    },
    onMutate: async (variables: MutationVariables) => {
      // we do not have all the information needed to optimistically
      // add a product tag, so skip the 'tag' mutationType
      if (
        variables.mutationType === 'add' ||
        variables.mutationType === 'edit'
      ) {
        return;
      }

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousMetadata =
        queryClient.getQueryData<IItemMetadata>(queryKey);

      if (!previousMetadata) {
        return;
      }

      let updatedProducts: Product[] = [];

      // Optimistically update to the new value
      switch (variables.mutationType) {
        case 'remove':
          updatedProducts = previousMetadata.productTags.filter(
            (product) => product.productStyleId !== variables.productStyleId,
          );
          break;
        case 'reorder':
          updatedProducts = variables.productList.map((product, index) => ({
            ...product,
            position: index + 1,
          }));
          break;
      }

      queryClient.setQueryData<IItemMetadata>(queryKey, {
        ...previousMetadata,
        productTags: updatedProducts,
      });

      // Return a context object with the snapshotted value
      return { previousMetadata };
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
    onSuccess: (data, variables) => {
      if (onSuccess) {
        onSuccess(data, variables);
      }
    },
  });
};
